import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { computeShipping } from '../checkout/computeShipping';
import { INDIAN_STATES } from '../data/indianStates';

import AdminToggle from './AdminToggle';
import { AdminPanelHero } from './AdminPanelUi';

const DEFAULT_OFFLINE_MSG =
  'Your order has been placed successfully. Your order number is {order_number}. We will contact you with payment details and shipping updates. You can also reach us using the contact details on our website.';
const DEFAULT_CHECKOUT_NOTE =
  'Online payment is not active yet. You can still place your order — we will share payment details after checkout.';

function CheckoutSection({ title, subtitle, children, action, className = '' }) {
  return (
    <section className={`admin-checkout-section${className ? ` ${className}` : ''}`}>
      <div className="admin-checkout-section__head">
        <div>
          <h2 className="admin-checkout-section__title">{title}</h2>
          {subtitle ? <p className="admin-checkout-section__subtitle">{subtitle}</p> : null}
        </div>
        {action ? <div className="admin-checkout-section__action">{action}</div> : null}
      </div>
      <div className="admin-checkout-section__body">{children}</div>
    </section>
  );
}

function MoneyTile({ id, label, hint, value, onChange, accent }) {
  return (
    <label
      className={`admin-checkout-money-tile${accent ? ` admin-checkout-money-tile--${accent}` : ''}`}
      htmlFor={id}
    >
      <span className="admin-checkout-money-tile__label">{label}</span>
      <span className="admin-checkout-money-tile__input-wrap">
        <span className="admin-checkout-money-tile__currency" aria-hidden>
          ₹
        </span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="form-control admin-checkout-money-tile__input"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder="0"
        />
      </span>
      {hint ? <span className="admin-checkout-money-tile__hint">{hint}</span> : null}
    </label>
  );
}

function ZoneRateCard({ id, zone, title, detail, value, onChange }) {
  return (
    <label className={`admin-checkout-zone-card admin-checkout-zone-card--${zone}`} htmlFor={id}>
      <span className="admin-checkout-zone-card__top">
        <span className="admin-checkout-zone-card__title">{title}</span>
        <span className="admin-checkout-zone-card__detail">{detail}</span>
      </span>
      <span className="admin-checkout-zone-card__input-wrap">
        <span className="admin-checkout-zone-card__currency">₹</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className="form-control admin-checkout-zone-card__input"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder="0"
        />
      </span>
    </label>
  );
}

export default function AdminSiteCheckoutFields({ sessionToken, setStatus }) {
  const [form, setForm] = useState({
    razorpay_enabled: false,
    razorpay_key_id: '',
    razorpay_key_secret: '',
    shipping_flat_inr: '0',
    shipping_zones_enabled: false,
    shipping_free_above_inr: '0',
    shipping_local_state: 'Gujarat',
    shipping_local_inr: '69',
    shipping_metro_inr: '89',
    shipping_remote_inr: '149',
    shipping_min_order_inr: '0',
    shipping_blocked_pincodes: '',
    offline_order_message: DEFAULT_OFFLINE_MSG,
    offline_checkout_note: DEFAULT_CHECKOUT_NOTE,
    order_auto_complete_days: '14',
    returns_enabled: true,
    returns_window_days: '7',
    returns_default_allowance: 'returnable',
    has_razorpay_secret: false,
    config_file_exists: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await fetch('/api/admin_checkout_settings.php', { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setLoadError(d.error || 'Could not load checkout settings.');
        return;
      }
      const s = d.settings || {};
      const rules = s.shipping_rules || {};
      const zonesOn = String(rules.mode || '') === 'zones';
      setForm({
        razorpay_enabled: Boolean(s.razorpay_enabled),
        razorpay_key_id: String(s.razorpay_key_id || ''),
        razorpay_key_secret: s.has_razorpay_secret ? '••••••••' : '',
        shipping_flat_inr: String(rules.flat_inr ?? s.shipping_flat_inr ?? 0),
        shipping_zones_enabled: zonesOn,
        shipping_free_above_inr: String(rules.free_above_inr ?? 0),
        shipping_local_state: String(rules.local_state || 'Gujarat'),
        shipping_local_inr: String(rules.local_inr ?? rules.gujarat_inr ?? 69),
        shipping_metro_inr: String(rules.metro_inr ?? 89),
        shipping_remote_inr: String(rules.remote_inr ?? 149),
        shipping_min_order_inr: String(rules.min_order_inr ?? 0),
        shipping_blocked_pincodes: Array.isArray(rules.blocked_pincodes)
          ? rules.blocked_pincodes.join('\n')
          : '',
        offline_order_message: String(s.offline_order_message || DEFAULT_OFFLINE_MSG),
        offline_checkout_note: String(s.offline_checkout_note || DEFAULT_CHECKOUT_NOTE),
        order_auto_complete_days: String(s.order_auto_complete_days ?? 14),
        returns_enabled: Boolean(s.returns_enabled),
        returns_window_days: String(s.returns_window_days ?? 7),
        returns_default_allowance: String(s.returns_default_allowance || 'returnable'),
        has_razorpay_secret: Boolean(s.has_razorpay_secret),
        config_file_exists: Boolean(s.config_file_exists),
      });
    } catch (e) {
      setLoadError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const previewRules = useMemo(
    () => ({
      mode: form.shipping_zones_enabled ? 'zones' : 'flat',
      flat_inr: Number(form.shipping_flat_inr) || 0,
      free_above_inr: Number(form.shipping_free_above_inr) || 0,
      local_state: form.shipping_local_state,
      local_inr: Number(form.shipping_local_inr) || 0,
      metro_inr: Number(form.shipping_metro_inr) || 0,
      remote_inr: Number(form.shipping_remote_inr) || 0,
      min_order_inr: Number(form.shipping_min_order_inr) || 0,
    }),
    [form]
  );

  const previewSamples = useMemo(() => {
    const subtotal = 1800;
    const home = computeShipping(previewRules, subtotal, 0, form.shipping_local_state, '380001');
    const metro = computeShipping(previewRules, subtotal, 0, 'Maharashtra', '411001');
    const remote = computeShipping(previewRules, subtotal, 0, 'Assam', '781001');
    return { subtotal, home, metro, remote };
  }, [previewRules, form.shipping_local_state]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus('');
    try {
      const r = await fetch('/api/admin_checkout_settings.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          session_token: sessionToken,
          razorpay_enabled: form.razorpay_enabled,
          razorpay_key_id: form.razorpay_key_id.trim(),
          razorpay_key_secret: form.razorpay_key_secret.trim(),
          shipping_flat_inr: Math.max(0, parseFloat(form.shipping_flat_inr) || 0),
          shipping_zones_enabled: form.shipping_zones_enabled,
          shipping_free_above_inr: Math.max(0, parseFloat(form.shipping_free_above_inr) || 0),
          shipping_local_state: form.shipping_local_state,
          shipping_local_inr: Math.max(0, parseFloat(form.shipping_local_inr) || 0),
          shipping_metro_inr: Math.max(0, parseFloat(form.shipping_metro_inr) || 0),
          shipping_remote_inr: Math.max(0, parseFloat(form.shipping_remote_inr) || 0),
          shipping_min_order_inr: Math.max(0, parseFloat(form.shipping_min_order_inr) || 0),
          shipping_blocked_pincodes: form.shipping_blocked_pincodes,
          offline_order_message: form.offline_order_message,
          offline_checkout_note: form.offline_checkout_note,
          order_auto_complete_days: Math.max(0, Math.min(365, parseInt(form.order_auto_complete_days, 10) || 0)),
          returns_enabled: form.returns_enabled,
          returns_window_days: Math.max(0, Math.min(365, parseInt(form.returns_window_days, 10) || 0)),
          returns_default_allowance: form.returns_default_allowance,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setStatus(d.error || 'Could not save checkout settings.');
        return;
      }
      const s = d.settings || {};
      setForm((f) => ({
        ...f,
        razorpay_enabled: Boolean(s.razorpay_enabled),
        razorpay_key_id: String(s.razorpay_key_id || ''),
        razorpay_key_secret: s.has_razorpay_secret ? '••••••••' : '',
        has_razorpay_secret: Boolean(s.has_razorpay_secret),
        config_file_exists: Boolean(s.config_file_exists),
        shipping_flat_inr: String(s.shipping_rules?.flat_inr ?? s.shipping_flat_inr ?? 0),
        shipping_zones_enabled: String(s.shipping_rules?.mode || '') === 'zones',
        shipping_free_above_inr: String(s.shipping_rules?.free_above_inr ?? 0),
        shipping_local_state: String(s.shipping_rules?.local_state || 'Gujarat'),
        shipping_local_inr: String(s.shipping_rules?.local_inr ?? s.shipping_rules?.gujarat_inr ?? 69),
        shipping_metro_inr: String(s.shipping_rules?.metro_inr ?? 89),
        shipping_remote_inr: String(s.shipping_rules?.remote_inr ?? 149),
        shipping_min_order_inr: String(s.shipping_rules?.min_order_inr ?? 0),
        shipping_blocked_pincodes: Array.isArray(s.shipping_rules?.blocked_pincodes)
          ? s.shipping_rules.blocked_pincodes.join('\n')
          : '',
        offline_order_message: String(s.offline_order_message || DEFAULT_OFFLINE_MSG),
        offline_checkout_note: String(s.offline_checkout_note || DEFAULT_CHECKOUT_NOTE),
        order_auto_complete_days: String(s.order_auto_complete_days ?? 14),
      }));
      setStatus('Checkout settings saved.');
    } catch (err) {
      setStatus(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-checkout-settings admin-checkout-settings--loading">
        <div className="spinner-border text-secondary" role="status" />
        <p className="small text-muted mb-0 mt-2">Loading checkout settings…</p>
      </div>
    );
  }

  const setZones = (enabled) => setForm((f) => ({ ...f, shipping_zones_enabled: enabled }));

  return (
    <form onSubmit={save} className="admin-checkout-settings">
      <AdminPanelHero
        title="Checkout & payments"
        lead="Set delivery fees, payment options, and messages customers see at checkout."
        badge={!form.config_file_exists ? 'Not saved yet' : null}
        badgeWarn={!form.config_file_exists}
      />

      {loadError ? <div className="alert alert-danger">{loadError}</div> : null}
      {!form.config_file_exists ? (
        <div className="alert alert-warning small mb-0">
          Save your settings once to apply them on your live store.
        </div>
      ) : null}

      <CheckoutSection
        title="Delivery fees"
        subtitle="Customers see the calculated fee in the order summary before they pay."
        action={
            <div className="admin-checkout-mode" role="group" aria-label="Shipping rate mode">
              <button
                type="button"
                className={`admin-checkout-mode__btn${!form.shipping_zones_enabled ? ' admin-checkout-mode__btn--active' : ''}`}
                onClick={() => setZones(false)}
              >
                Simple flat
              </button>
              <button
                type="button"
                className={`admin-checkout-mode__btn${form.shipping_zones_enabled ? ' admin-checkout-mode__btn--active' : ''}`}
                onClick={() => setZones(true)}
              >
                By zone
              </button>
            </div>
          }
        >
          <div className="admin-checkout-money-grid">
            <MoneyTile
              id="checkout-shipping"
              label={form.shipping_zones_enabled ? 'Rest of India' : 'Every order'}
              hint={form.shipping_zones_enabled ? 'Default zone fee' : 'Same fee all India'}
              value={form.shipping_flat_inr}
              onChange={(v) => setForm((f) => ({ ...f, shipping_flat_inr: v }))}
              accent="base"
            />
            <MoneyTile
              id="checkout-shipping-free"
              label="Free above"
              hint="0 = never free"
              value={form.shipping_free_above_inr}
              onChange={(v) => setForm((f) => ({ ...f, shipping_free_above_inr: v }))}
              accent="free"
            />
            <MoneyTile
              id="checkout-shipping-min"
              label="Min. order"
              hint="0 = no minimum"
              value={form.shipping_min_order_inr}
              onChange={(v) => setForm((f) => ({ ...f, shipping_min_order_inr: v }))}
            />
          </div>

          {form.shipping_zones_enabled ? (
            <div className="admin-checkout-zones">
              <p className="admin-checkout-zones__intro">
                Choose the state you ship from — customers there get your local delivery rate.
              </p>
              <div className="admin-checkout-zone-grid">
                <label className="admin-checkout-home-state" htmlFor="checkout-shipping-local-state">
                  <span className="admin-checkout-home-state__label">Home state</span>
                  <select
                    id="checkout-shipping-local-state"
                    className="form-select"
                    value={form.shipping_local_state}
                    onChange={(e) => setForm((f) => ({ ...f, shipping_local_state: e.target.value }))}
                  >
                    {INDIAN_STATES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </label>
                <ZoneRateCard
                  id="checkout-shipping-local-inr"
                  zone="local"
                  title="Local rate"
                  detail="Usually lowest"
                  value={form.shipping_local_inr}
                  onChange={(v) => setForm((f) => ({ ...f, shipping_local_inr: v }))}
                />
                <ZoneRateCard
                  id="checkout-shipping-metro"
                  zone="metro"
                  title="Metro & major"
                  detail="MH, DL, KA, TN…"
                  value={form.shipping_metro_inr}
                  onChange={(v) => setForm((f) => ({ ...f, shipping_metro_inr: v }))}
                />
                <ZoneRateCard
                  id="checkout-shipping-remote"
                  zone="remote"
                  title="Remote / NE"
                  detail="Hills & islands"
                  value={form.shipping_remote_inr}
                  onChange={(v) => setForm((f) => ({ ...f, shipping_remote_inr: v }))}
                />
              </div>
            </div>
          ) : (
            <p className="admin-checkout-flat-note small text-muted mb-0">
              One fee for all PIN codes (free-delivery and minimum order rules still apply).
            </p>
          )}

          <div className="admin-checkout-preview-strip" aria-label="Example delivery fees">
            <div className="admin-checkout-preview-strip__head">
              <span className="admin-checkout-preview-strip__title">Live preview</span>
              <span className="admin-checkout-preview-strip__sub">
                Sample cart ₹{previewSamples.subtotal.toLocaleString('en-IN')}
              </span>
            </div>
            <ul className="admin-checkout-preview-strip__grid">
              {form.shipping_zones_enabled ? (
                <>
                  <li className="admin-checkout-preview-strip__card">
                    <span className="admin-checkout-preview-strip__label">{form.shipping_local_state}</span>
                    <strong className="admin-checkout-preview-strip__fee">
                      {previewSamples.home.ok
                        ? previewSamples.home.free
                          ? 'Free'
                          : `₹${previewSamples.home.fee_inr}`
                        : '—'}
                    </strong>
                  </li>
                  <li className="admin-checkout-preview-strip__card">
                    <span className="admin-checkout-preview-strip__label">Maharashtra</span>
                    <strong className="admin-checkout-preview-strip__fee">
                      {previewSamples.metro.ok
                        ? previewSamples.metro.free
                          ? 'Free'
                          : `₹${previewSamples.metro.fee_inr}`
                        : '—'}
                    </strong>
                  </li>
                  <li className="admin-checkout-preview-strip__card">
                    <span className="admin-checkout-preview-strip__label">Assam (remote)</span>
                    <strong className="admin-checkout-preview-strip__fee">
                      {previewSamples.remote.ok
                        ? previewSamples.remote.free
                          ? 'Free'
                          : `₹${previewSamples.remote.fee_inr}`
                        : '—'}
                    </strong>
                  </li>
                </>
              ) : (
                <li className="admin-checkout-preview-strip__card">
                  <span className="admin-checkout-preview-strip__label">Any state</span>
                  <strong className="admin-checkout-preview-strip__fee">
                    {previewSamples.home.ok
                      ? previewSamples.home.free
                        ? 'Free'
                        : `₹${previewSamples.home.fee_inr}`
                      : '—'}
                  </strong>
                </li>
              )}
            </ul>
          </div>

          <details className="admin-checkout-details">
            <summary>Blocked PIN codes (optional)</summary>
            <textarea
              id="checkout-shipping-blocked"
              className="form-control font-monospace small mt-2"
              rows={3}
              placeholder="One 6-digit PIN per line"
              value={form.shipping_blocked_pincodes}
              onChange={(e) => setForm((f) => ({ ...f, shipping_blocked_pincodes: e.target.value }))}
            />
          </details>
      </CheckoutSection>

      <div className="admin-checkout-aux-grid">
        <CheckoutSection
          className="admin-checkout-section--returns"
          title="Returns"
          subtitle="Customers can request returns for eligible items; you approve and refund online payments via Razorpay."
        >
          <AdminToggle
            id="checkout-returns-enabled"
            label="Enable returns"
            checked={form.returns_enabled}
            onChange={(v) => setForm((f) => ({ ...f, returns_enabled: v }))}
          />
          <label className="admin-checkout-inline-field mt-3" htmlFor="checkout-returns-window">
            <span className="admin-checkout-inline-field__label">Return window (days)</span>
            <input
              id="checkout-returns-window"
              type="number"
              min={0}
              max={365}
              className="form-control admin-checkout-inline-field__input"
              value={form.returns_window_days}
              onChange={(e) => setForm((f) => ({ ...f, returns_window_days: e.target.value }))}
            />
            <span className="admin-checkout-inline-field__hint">From payment date · 0 = no limit</span>
          </label>
          <label className="form-label small mt-3 mb-1" htmlFor="checkout-returns-default">
            Default when catalog levels are &quot;Inherit&quot;
          </label>
          <select
            id="checkout-returns-default"
            className="form-select form-select-sm"
            value={form.returns_default_allowance}
            onChange={(e) => setForm((f) => ({ ...f, returns_default_allowance: e.target.value }))}
          >
            <option value="returnable">Returnable</option>
            <option value="non_returnable">Non-returnable</option>
          </select>
          <p className="form-text mb-0 mt-2">
            Fine-tune per category, shop group, product type, or product under Admin → Categories / Products.
          </p>
        </CheckoutSection>

        <CheckoutSection
          title="After delivery"
          subtitle="Mark orders as delivered automatically after shipping."
        >
          <label className="admin-checkout-inline-field" htmlFor="checkout-auto-complete-days">
            <span className="admin-checkout-inline-field__label">Days until Completed</span>
            <input
              id="checkout-auto-complete-days"
              type="number"
              min={0}
              max={365}
              step={1}
              className="form-control admin-checkout-inline-field__input"
              value={form.order_auto_complete_days}
              onChange={(e) => setForm((f) => ({ ...f, order_auto_complete_days: e.target.value }))}
            />
            <span className="admin-checkout-inline-field__hint">0 = off</span>
          </label>
        </CheckoutSection>

        <CheckoutSection title="Bank / UPI orders" subtitle="When Razorpay is off.">
          <label className="form-label small" htmlFor="checkout-offline-note">
            Checkout page note
          </label>
          <textarea
            id="checkout-offline-note"
            className="form-control form-control-sm mb-3"
            rows={2}
            value={form.offline_checkout_note}
            onChange={(e) => setForm((f) => ({ ...f, offline_checkout_note: e.target.value }))}
          />
          <label className="form-label small" htmlFor="checkout-offline-msg">
            Order placed message
          </label>
          <textarea
            id="checkout-offline-msg"
            className="form-control form-control-sm"
            rows={3}
            value={form.offline_order_message}
            onChange={(e) => setForm((f) => ({ ...f, offline_order_message: e.target.value }))}
          />
          <p className="form-text mb-0">
            Add {'{order_number}'} in the text where the customer&apos;s order number should appear.
          </p>
        </CheckoutSection>

        <CheckoutSection
          className="admin-checkout-section--razorpay"
          title="Razorpay"
          subtitle="Online payment at checkout."
          action={
          <AdminToggle
            id="checkout-rz-enabled"
            label="Enabled"
            checked={form.razorpay_enabled}
            onChange={(v) => setForm((f) => ({ ...f, razorpay_enabled: v }))}
          />
        }
      >
        {!form.razorpay_enabled ? (
          <p className="admin-checkout-razorpay-off small mb-3">Off — customers place orders without paying online.</p>
        ) : (
          <p className="small text-muted mb-3">
            Use live keys from the Razorpay dashboard (Test mode for staging). Both Key ID and Secret are required.
            Paid orders show Razorpay order/payment ids in Orders and Analytics.
          </p>
        )}
        <div className={`admin-checkout-razorpay-keys${form.razorpay_enabled ? '' : ' admin-checkout-razorpay-keys--disabled'}`}>
          <div className="admin-checkout-razorpay-keys__field">
            <label className="form-label small" htmlFor="checkout-rz-key">
              Key ID
            </label>
            <input
              id="checkout-rz-key"
              className="form-control"
              value={form.razorpay_key_id}
              onChange={(e) => setForm((f) => ({ ...f, razorpay_key_id: e.target.value }))}
              autoComplete="off"
              disabled={!form.razorpay_enabled}
            />
          </div>
          <div className="admin-checkout-razorpay-keys__field">
            <label className="form-label small" htmlFor="checkout-rz-secret">
              Key secret
            </label>
            <input
              id="checkout-rz-secret"
              type="password"
              className="form-control"
              value={form.razorpay_key_secret}
              onChange={(e) => setForm((f) => ({ ...f, razorpay_key_secret: e.target.value }))}
              placeholder={form.has_razorpay_secret ? 'Leave •••• to keep current' : 'Enter secret'}
              autoComplete="new-password"
              disabled={!form.razorpay_enabled}
            />
          </div>
        </div>
      </CheckoutSection>
      </div>

      <div className="admin-checkout-savebar">
        <button type="submit" className="btn btn-primary px-4" disabled={saving}>
          {saving ? 'Saving…' : 'Save checkout settings'}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={load} disabled={saving}>
          Reload
        </button>
      </div>
    </form>
  );
}
